import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  getUserProfile,
  updateUserProfile,
  uploadProfilePicture,
} from "../../api/user";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import { toast, ToastContainer } from "react-toastify";
import ProfileImageUpload from "../../components/ProfileImageUpload";
import Loader from "../../components/Loader";
import ChangePasswordModal from "../../components/ChangePasswordModal";

const ProfilePage = () => {
  const [user, setUser] = useState<any>({
    firstName: "",
    phone: "",
    email: "",
    dob: "",
    postalCode: "",
    profilePicture: "",
  });
  // Persisted data used for Topbar (only updates after save/refetch)
  const [displayUser, setDisplayUser] = useState<any>({
    firstName: "",
    profilePicture: "",
  });
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dob?: string;
  }>({});

  // Guard against double API calls in React StrictMode
  const fetchedRef = useRef(false);

  // Normalize API payload into the local user state shape
  const applyProfilePayload = (payload: any) => {
    const name: string = payload?.name || "";
    const nameParts = name.trim().split(" ");

    const mapped = {
      firstName: payload?.firstName || nameParts[0] || "",
      email: payload?.email ?? "",
      phone: payload?.phone ?? payload?.mobile ?? "",
      dob: payload?.DOB ?? payload?.dob ?? "",
      profilePicture:
        payload?.profilePictureUrl ??
        payload?.profilePicture ??
        "",
    };

    // Update form state
    setUser((prev: any) => ({
      ...prev,
      ...mapped,
    }));
    // Update display state separately so Topbar changes only after save/refetch
    setDisplayUser((prev: any) => ({
      ...prev,
      firstName: mapped.firstName,
      profilePicture: mapped.profilePicture,
    }));
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchProfile = async () => {
      try {
        const { data } = await getUserProfile();
        const payload = data?.data || data || {};
        applyProfilePayload(payload);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        toast.error("Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleFileSelect = async (file: File) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const res = await uploadProfilePicture(file);
      if (res.status === 201) {
        toast.success("Profile picture updated!");

        // Update the profile picture immediately
        setUser((prev: any) => ({
          ...prev,
          profilePicture:
            res?.data?.profilePictureUrl ??
            res?.data?.profilePicture ??
            prev.profilePicture,
        }));

        // Re-fetch profile data to get the latest information
        try {
          const { data } = await getUserProfile();
          const payload = data?.data || data || {};
          applyProfilePayload(payload);
        } catch (fetchErr) {
          console.error("Failed to refresh profile data:", fetchErr);
          // Don't show error to user as the image was uploaded successfully
        }
      } else {
        toast.error(res.data.message || "Failed to update profile picture!");
      }
    } catch (err) {
      toast.error("Failed to update profile picture!");
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Clear field-level error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // For phone field, only allow digits and limit to 10 digits
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setUser((prev: any) => ({
        ...prev,
        [name]: digitsOnly,
      }));
    } else {
      setUser((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validate = () => {
    const errors: { [key: string]: string } = {};

    if (!user) {
      errors.firstName = "First name is required";

      errors.email = "Email is required";
      errors.phone = "Phone number is required";
      errors.dob = "Date of birth is required";
      setErrors(errors);
      return false;
    }

    if (!user.firstName) errors.firstName = "First name is required";
    if (!user.email) errors.email = "Email is required";
    if (!user.phone) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(user.phone.replace(/\D/g, ""))) {
      errors.phone = "Please enter a valid 10-digit mobile number";
    }
    if (!user.dob) errors.dob = "Date of birth is required";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await updateUserProfile(
        user.firstName,
        user.email,
        user.phone,
        user.dob
      );
      if (res.status === 201) {
        toast.success(res.data.message || "Profile updated successfully!");
        // Refresh profile so Topbar and form show latest data from server
        try {
          const { data } = await getUserProfile();
          const payload = data?.data || data || {};
          applyProfilePayload(payload);
        } catch (fetchErr) {
          console.error("Failed to refresh profile after save:", fetchErr);
        }
        setIsEditing(false);
      } else {
        toast.error(res.data.message || "Failed to update profile!");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to update profile!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <Topbar
          toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          displayName={displayUser?.firstName}
          profilePicture={displayUser?.profilePicture}
        />

        {/* Page Content */}
        <main className="p-4 md:p-6 overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white/80 backdrop-blur-sm border border-white/20 p-8 md:p-10 flex flex-col lg:flex-row gap-8 md:gap-10"
            >
              {/* Left Section: Profile Picture */}
              <div className="flex flex-col items-center lg:w-1/3 gap-6">
                <ProfileImageUpload
                  profilePicture={user?.profilePicture || ""}
                  onFileSelect={handleFileSelect}
                  isUploading={isUploading}
                  isEditing={isEditing}
                />
              </div>

              {/* Right Section: Profile Form */}
              <div className="flex-1 space-y-8">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="brand-heading">
                    Profile Details
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Manage your personal information
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={user?.firstName || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`mt-1 w-full p-3 border rounded-xl disabled:bg-gray-50 transition-all duration-200 ${errors.firstName ? "border-red-500" : "border-gray-200"
                        }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={user?.email || ""}
                      disabled // Email usually not editable
                      className="mt-1 w-full p-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Phone<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={user?.phone || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      maxLength={10}
                      placeholder="Enter 10-digit mobile number"
                      className={`mt-1 w-full p-3 border rounded-xl disabled:bg-gray-50 transition-all duration-200 ${errors.phone ? "border-red-500" : "border-gray-200"
                        }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Date of Birth<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={user?.dob || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`mt-1 w-full p-3 border rounded-xl disabled:bg-gray-50 transition-all duration-200 ${errors.dob ? "border-red-500" : "border-gray-200"
                        }`}
                    />
                    {errors.dob && (
                      <p className="text-red-500 text-sm mt-1">{errors.dob}</p>
                    )}
                  </div>

                  {/* Address section was removed by user edits; keeping current design unchanged */}
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col sm:flex-row justify-end gap-4 pt-6"
                >
                  {!isEditing ? (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        Edit Profile
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowChangePassword(true)}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        Change Password
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSave()}
                        className="px-4 py-2 rounded-xl bg-[#016683] text-white font-semibold hover:opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        Save Changes
                      </motion.button>
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </main>
      </div>
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default ProfilePage;
